/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import {Box, SvgIcon} from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import { withTour } from '@reactour/tour';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as Actions from '../actions/actions';
import FilterName from './utils/FilterName';
import LicenseNotification from './utils/LicenseNotification';
import ServiceStatusNotification from './utils/ServiceStatusNotification';
import NotificationsComponent from './utils/NotificationsComponent';
import HelpButton from './utils/HelpButton';
import TourButton from './utils/TourButton';
import { UserMenu } from './menus/UserMenu';
import Typography from '@mui/material/Typography';
import UserAvatar from './utils/UserAvatar';

const useExperimental = (setAppState) => {
	useEffect(() => setAppState({ experimental: localStorage.getItem('experimental') === 'true' }), []);
};

function StreamSheetAppBarComponent(props) {
	const [filter, setFilter] = useState('');
	// const { setIsOpen, setCurrentStep, setSteps } = useTour();

	useExperimental(props.setAppState);

	useEffect(() => {
		if (props.onFilterChange) {
			props.onFilterChange(filter);
		}
	}, [filter]);

	return (
		<AppBar
			// TODO: color={props.isMachineEngineConnected ? 'primary' : 'error'}
			color="primary"
			sx={{
				display: 'flex',
				margin: 0,
				padding: 0,
				position: 'relative',
				zIndex: (theme) => theme.zIndex.drawer + 1,
			}}
			elevation={0}
		>
			<div
				style={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					height: '50px'
				}}
			>
				<LicenseNotification />
				<ServiceStatusNotification />
				<div style={{ display: 'flex', flexGrow: 1, alignItems: 'center' }}>
					<Box
						style={{ padding: '0px', margin: '19px 38px 6px 16px', width: '185px', height: '40px' }}
						color="inherit"
						size="large"
					>
						<SvgIcon style={{ color: '#FD602E', width: '185px', height: '25px' }} viewBox="0 0 188.88 22.5">
							<path
								d="M11.01,8.37l-2.46,2.47c-1-1-1.92-1.5-2.75-1.5-.45,0-.8,.09-1.05,.28-.25,.19-.37,.43-.37,.71,0,.21,.07,.41,.23,.59,.17,.17,.57,.42,1.19,.74l1.47,.73c1.55,.76,2.61,1.53,3.18,2.32,.57,.79,.86,1.72,.86,2.78,0,1.42-.52,2.6-1.56,3.54-1.04,.95-2.43,1.42-4.17,1.42-2.32,0-4.18-.91-5.57-2.72l2.46-2.68c.47,.55,1.01,.99,1.64,1.33,.63,.33,1.2,.5,1.68,.5,.53,0,.95-.13,1.27-.38,.32-.25,.48-.55,.48-.88,0-.62-.58-1.21-1.74-1.8l-1.35-.67c-2.58-1.3-3.86-2.93-3.86-4.88,0-1.26,.48-2.33,1.45-3.22,.98-.89,2.22-1.34,3.73-1.34,1.04,0,2,.23,2.91,.68,.92,.45,1.69,1.1,2.33,1.95v.02Zm0,0"
							/>
							<path d="M14.92,.28h3.98V6.15h2.37v3.44h-2.37v12.49h-3.98V9.59h-2.05v-3.44h2.05V.28Zm0,0" />
							<path d="M22.49,6.15h3.43v2c.37-.79,.86-1.39,1.48-1.8,.61-.41,1.29-.61,2.02-.61,.52,0,1.06,.14,1.62,.41l-1.24,3.44c-.47-.23-.86-.35-1.16-.35-.62,0-1.14,.38-1.57,1.15-.42,.76-.63,2.25-.63,4.48v7.22h-3.95V6.15Zm0,0" />
							<path
								d="M48.99,15.28h-12.85c.18,1.13,.68,2.04,1.48,2.71,.81,.66,1.84,1,3.09,1,1.49,0,2.78-.52,3.85-1.56l3.37,1.58c-.84,1.19-1.84,2.07-3.02,2.65-1.17,.57-2.56,.85-4.17,.85-2.5,0-4.53-.79-6.1-2.36-1.57-1.58-2.36-3.56-2.36-5.93s.78-4.45,2.34-6.05c1.57-1.61,3.54-2.41,5.9-2.41,2.51,0,4.55,.8,6.13,2.41,1.57,1.6,2.36,3.72,2.36,6.36v.76Zm-4.02-3.15c-.26-.89-.78-1.61-1.57-2.16-.77-.55-1.67-.83-2.69-.83-1.11,0-2.09,.31-2.93,.93-.53,.39-1.01,1.08-1.46,2.06h8.65Zm0,0"
							/>
							<path
								d="M64,6.15h3.98v15.94h-3.98v-1.68c-.78,.74-1.57,1.28-2.36,1.61-.78,.32-1.63,.48-2.55,.48-2.06,0-3.84-.79-5.35-2.39-1.5-1.6-2.25-3.59-2.25-5.96s.73-4.48,2.18-6.05c1.45-1.57,3.22-2.35,5.3-2.35,.96,0,1.86,.18,2.69,.54,.84,.36,1.62,.9,2.33,1.62v-1.76Zm-4.2,3.28c-1.24,0-2.27,.44-3.09,1.32-.82,.87-1.23,1.98-1.23,3.36s.41,2.5,1.24,3.39c.84,.89,1.87,1.33,3.09,1.33s2.3-.43,3.13-1.3c.83-.88,1.25-2.03,1.25-3.44s-.41-2.51-1.25-3.37c-.83-.86-1.88-1.29-3.15-1.29h0Zm0,0"
							/>
							<path
								d="M72,6.15h4.01v1.83c.68-.75,1.44-1.31,2.27-1.68,.84-.37,1.75-.55,2.74-.55s1.89,.24,2.7,.73c.8,.49,1.44,1.2,1.93,2.14,.63-.93,1.41-1.65,2.33-2.14,.93-.49,1.94-.73,3.03-.73s2.13,.26,2.99,.79c.86,.53,1.47,1.21,1.84,2.07,.38,.85,.57,2.23,.57,4.14v3.18h-4.03v-1.91c0-1.8-.22-3.02-.67-3.66-.45-.64-1.12-.97-2.02-.97-.68,0-1.3,.19-1.85,.59-.54,.39-.93,.93-1.2,1.62-.26,.68-.39,1.79-.39,3.31v1.02h-4.03v-1.55c0-1.42-.11-2.45-.32-3.09-.2-.64-.51-1.12-.93-1.43-.42-.31-.93-.47-1.52-.47-.66,0-1.27,.2-1.81,.6-.54,.39-.94,.94-1.22,1.66-.27,.71-.39,1.83-.39,3.37v.92h-4.01V6.15Zm0,0"
							/>
							<path
								d="M109.99,8.38l-2.48,2.48c-1-1-1.92-1.5-2.74-1.5-.45,0-.8,.1-1.05,.29-.25,.19-.38,.42-.38,.7,0,.21,.08,.41,.23,.6,.16,.18,.56,.42,1.2,.73l1.46,.73c1.54,.76,2.6,1.54,3.18,2.33,.58,.79,.86,1.71,.86,2.78,0,1.42-.52,2.6-1.57,3.54-1.03,.95-2.42,1.42-4.17,1.42-2.32,0-4.18-.91-5.56-2.72l2.46-2.68c.47,.55,1.01,.99,1.64,1.33,.63,.33,1.19,.5,1.68,.5,.53,0,.95-.12,1.27-.38,.32-.25,.48-.55,.48-.88,0-.61-.58-1.21-1.74-1.8l-1.35-.67c-2.58-1.3-3.87-2.92-3.87-4.88,0-1.26,.48-2.33,1.45-3.22,.98-.89,2.22-1.34,3.73-1.34,1.03,0,2,.23,2.91,.68,.91,.45,1.69,1.1,2.33,1.95h0Zm0,0"
							/>
							<path
								d="M113.44,0h3.97V7.76c.78-.67,1.57-1.18,2.35-1.51,.79-.34,1.59-.51,2.4-.51,1.58,0,2.91,.55,4,1.64,.93,.95,1.39,2.34,1.39,4.17v10.53h-3.94v-6.99c0-1.85-.09-3.09-.26-3.75-.17-.65-.48-1.14-.91-1.46-.42-.32-.94-.48-1.56-.48-.81,0-1.51,.26-2.09,.8-.57,.54-.97,1.27-1.2,2.2-.12,.48-.18,1.57-.18,3.28v6.4h-3.97V0Zm0,0"
							/>
							<path
								d="M147.4,15.28h-12.85c.19,1.13,.68,2.04,1.48,2.71,.81,.66,1.84,1,3.09,1,1.5,0,2.78-.52,3.86-1.56l3.37,1.58c-.84,1.19-1.84,2.07-3.02,2.65-1.17,.57-2.56,.85-4.18,.85-2.5,0-4.53-.79-6.11-2.36-1.57-1.58-2.36-3.56-2.36-5.93s.78-4.45,2.34-6.05c1.57-1.61,3.54-2.41,5.9-2.41,2.51,0,4.55,.8,6.12,2.41,1.57,1.6,2.35,3.72,2.35,6.36v.76Zm-4.02-3.15c-.26-.89-.78-1.61-1.56-2.16-.77-.55-1.67-.83-2.7-.83-1.11,0-2.09,.31-2.93,.93-.53,.39-1.02,1.08-1.46,2.06h8.65Zm0,0"
							/>
							<path
								d="M166.6,15.28h-12.85c.19,1.13,.68,2.04,1.48,2.71,.81,.66,1.84,1,3.09,1,1.49,0,2.78-.52,3.85-1.56l3.37,1.58c-.84,1.19-1.84,2.07-3.02,2.65-1.17,.57-2.56,.85-4.17,.85-2.5,0-4.53-.79-6.1-2.36-1.57-1.58-2.36-3.56-2.36-5.93s.78-4.45,2.34-6.05c1.57-1.61,3.54-2.41,5.9-2.41,2.51,0,4.55,.8,6.12,2.41,1.57,1.6,2.35,3.72,2.35,6.36v.76Zm-4.02-3.15c-.26-.89-.78-1.61-1.57-2.16-.77-.55-1.67-.83-2.69-.83-1.11,0-2.09,.31-2.93,.93-.53,.39-1.01,1.08-1.46,2.06h8.65Zm0,0"
							/>
							<path
								d="M170.32,.28h3.98V6.15h2.38v3.44h-2.38v12.49h-3.98V9.59h-2.05v-3.44h2.05V.28Zm0,0"
							/>
							<path
								d="M188.59,8.38l-2.48,2.48c-1-1-1.92-1.5-2.74-1.5-.44,0-.8,.1-1.05,.29-.25,.19-.38,.42-.38,.7,0,.21,.08,.41,.23,.6,.16,.18,.56,.42,1.2,.73l1.46,.73c1.54,.76,2.6,1.54,3.18,2.33,.58,.79,.86,1.71,.86,2.78,0,1.42-.52,2.6-1.56,3.54-1.03,.95-2.42,1.42-4.17,1.42-2.32,0-4.18-.91-5.56-2.72l2.46-2.68c.47,.55,1.02,.99,1.64,1.33,.63,.33,1.19,.5,1.68,.5,.53,0,.95-.12,1.27-.38,.32-.25,.48-.55,.48-.88,0-.61-.58-1.21-1.74-1.8l-1.35-.67c-2.58-1.3-3.87-2.92-3.87-4.88,0-1.26,.48-2.33,1.45-3.22,.98-.89,2.22-1.34,3.73-1.34,1.04,0,2,.23,2.91,.68,.92,.45,1.69,1.1,2.33,1.95h0Zm0,0"
							/>
							<rect x="72.41" y="17.42" width="3.23" height="4.51" />
							<rect x="82.58" y="17.42" width="3.23" height="4.51" />
							<rect x="92.78" y="17.42" width="3.23" height="4.51" />
							<rect x="77.51" y="17.42" width="3.23" height="4.51" />
							<rect x="87.68" y="17.42" width="3.23" height="4.51" />
						</SvgIcon>
					</Box>
					<Box sx={{display: 'flex', marginTop: '4px'}}>
						{props.nameEditor}
						{props.appState ? (
							<Typography
								sx={{
									marginTop: '4px',
									width: '130px',
									marginLeft: '5px',
									fontSize: '1.2rem',
									lineHeight: '1.4735',
									color: (theme) =>
										`${theme.components.MuiAppBar.styleOverrides.colorIcon.backgroundColor}, 1)`
								}}
							>
								{props.appState}
							</Typography>
						) : null}
						{props.appControl}
					</Box>
				</div>
				{props.showFilter ? (
					<div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<FilterName filter={filter} onUpdateFilter={setFilter} />
					</div>
				) : null}
				<div style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
					<Toolbar
						style={{
							paddingRight: '5px',
							minHeight: '50px'
						}}
					>
						{props.users ? <UserAvatar users={props.users}/> : null}
						<NotificationsComponent />
						<HelpButton helpContext={props.helpContext} />
						<TourButton
							app={!!props.appState}
							runTour={(steps) => {
								props.setSteps(steps)
								props.setCurrentStep(0, steps);
								props.setIsOpen(true);
							}}
						/>
						<UserMenu />
					</Toolbar>
				</div>
			</div>
		</AppBar>
	);
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators({ ...Actions }, dispatch);
}

export const StreamSheetAppBar = withTour(connect(null, mapDispatchToProps)(StreamSheetAppBarComponent));
